import { language } from '../../services/LanguageService';
import { showDirectory, resumeTransfer, pauseTransfer, cancelTransferStarter, getTransferSystemData, forceUpdateTransferMonitor, setRemoteTransfer, runningWithAsperaMobile, sendMobileSdkMessage, modifyTransfer } from '../../helpers/transfer';
import { SubManager } from '../SubManager';
import { AsperaError } from '../AsperaError';
import { notification } from '../../services/NotificationService';
import { getEnvironmentVar } from '../../helpers/helpers';
import { tracking } from '../../services/TrackingService';
import { getTimeString, nodeTransferToConnectTransfer } from '../../helpers/starterHelpers';
/**
 * Create an Aspera Transfer item
 */
export class AsperaTransfer {
    constructor(transfer) {
        /** Subscription manager for events */
        this.subManager = new SubManager();
        /** Pause a transfer */
        this.pause = () => {
            const transferFunction = getTransferSystemData().pauseRemoteTransfer;
            let callToUse;
            const originalStatus = this.status;
            if (this.connectTransfer) {
                this.connectTransfer.status = 'pausing';
                callToUse = pauseTransfer(this.id);
            }
            else if (this.nodeTransfer && transferFunction) {
                this.nodeTransfer.status = 'pausing';
                callToUse = transferFunction(this);
            }
            else {
                return;
            }
            forceUpdateTransferMonitor();
            this.subManager.setSubscription(callToUse.subscribe({
                next: () => {
                    notification.create('info', language.get('transferActivity.transferPaused'), this.nodeTransfer ? language.get('transferActivity.remoteTransferHelper') : undefined);
                    tracking.track('Started Process', { process: 'Pause Transfer', successFlag: true, processType: 'transfer' });
                    forceUpdateTransferMonitor();
                },
                error: error => {
                    if (this.nodeTransfer) {
                        this.nodeTransfer.status = originalStatus;
                    }
                    else if (this.connectTransfer) {
                        this.connectTransfer.status = originalStatus;
                    }
                    new AsperaError('AsperaTransfer: failed to pauseTransfer', error);
                    notification.create('error', language.get('transferActivity.unablePause'));
                    tracking.track('Started Process', { process: 'Pause Transfer', successFlag: false, processType: 'transfer' });
                    forceUpdateTransferMonitor();
                },
            }));
        };
        /** Cancel a transfer */
        this.cancel = () => {
            const transferFunction = getTransferSystemData().cancelRemoteTransfer;
            let callToUse;
            const originalStatus = this.status;
            if (this.connectTransfer) {
                this.connectTransfer.status = 'canceling';
                callToUse = cancelTransferStarter(this.id);
            }
            else if (this.nodeTransfer && transferFunction) {
                this.nodeTransfer.status = 'canceling';
                callToUse = transferFunction(this);
            }
            else {
                return;
            }
            forceUpdateTransferMonitor();
            this.subManager.setSubscription(callToUse.subscribe({
                next: () => {
                    notification.create('info', language.get('transferActivity.transferCanceled'), this.nodeTransfer ? language.get('transferActivity.remoteTransferHelper') : undefined);
                    tracking.track('Started Process', { process: 'Cancel Transfer', successFlag: true, processType: 'transfer' });
                    forceUpdateTransferMonitor();
                },
                error: error => {
                    if (this.nodeTransfer) {
                        this.nodeTransfer.status = originalStatus;
                    }
                    else if (this.connectTransfer) {
                        this.connectTransfer.status = originalStatus;
                    }
                    new AsperaError('AsperaTransfer: failed to cancel transfer', error);
                    notification.create('error', language.get('transferActivity.unableCancel'));
                    tracking.track('Started Process', { process: 'Cancel Transfer', successFlag: false, processType: 'transfer' });
                    forceUpdateTransferMonitor();
                },
            }));
        };
        /**
         * Update a transfer
         * @param data - new date to update
         */
        this.updateTransfer = (data) => {
            if (!this.canUpdateTransfer) {
                return;
            }
            this.subManager.setSubscription(modifyTransfer(this.id, data).subscribe({
                next: () => {
                    tracking.track('Started Process', { process: 'Modify Transfer', successFlag: true, processType: 'transfer' });
                    forceUpdateTransferMonitor();
                },
                error: error => {
                    new AsperaError('AsperaTransfer: failed to modifyTransfer', error);
                    notification.create('error', language.get('transferActivity.unableModify'));
                    tracking.track('Started Process', { process: 'Modify Transfer', successFlag: false, processType: 'transfer' });
                    forceUpdateTransferMonitor();
                },
            }));
        };
        /** Resume a transfer */
        this.resume = () => {
            const transferFunction = getTransferSystemData().resumeRemoteTransfer;
            let callToUse;
            const originalStatus = this.status;
            if (this.connectTransfer) {
                this.connectTransfer.status = 'resuming';
                callToUse = resumeTransfer(this.id);
            }
            else if (this.nodeTransfer && transferFunction) {
                this.nodeTransfer.status = 'resuming';
                callToUse = transferFunction(this);
            }
            else {
                return;
            }
            forceUpdateTransferMonitor();
            this.subManager.setSubscription(callToUse.subscribe({
                next: (url) => {
                    notification.create('info', language.get('transferActivity.transferResumed'), this.nodeTransfer ? language.get('transferActivity.remoteTransferHelper') : undefined);
                    tracking.track('Started Process', { process: 'Resume Transfer', successFlag: true, processType: 'transfer' });
                    forceUpdateTransferMonitor();
                    if (typeof url === 'string') {
                        setRemoteTransfer(this.id, url, true);
                    }
                },
                error: error => {
                    if (this.nodeTransfer) {
                        this.nodeTransfer.status = originalStatus;
                    }
                    else if (this.connectTransfer) {
                        this.connectTransfer.status = originalStatus;
                    }
                    new AsperaError('AsperaTransfer: failed to resumeTransfer', error);
                    notification.create('error', language.get('transferActivity.unableResume'));
                    tracking.track('Started Process', { process: 'Resume Transfer', successFlag: false, processType: 'transfer' });
                    forceUpdateTransferMonitor();
                },
            }));
        };
        /** #ConnectOnly Open the system folder of a transfer */
        this.openLocalFolder = () => {
            if (runningWithAsperaMobile()) {
                sendMobileSdkMessage({ purpose: 'SHOW_DOWNLOADS' });
                return;
            }
            this.subManager.setSubscription(showDirectory(this.id).subscribe({
                next: () => {
                    tracking.track('Started Process', { process: 'Open Local Folder', successFlag: true, processType: 'transfer' });
                },
                error: error => {
                    tracking.track('Started Process', { process: 'Open Local Folder', successFlag: false, processType: 'transfer' });
                    new AsperaError('AsperaTransfer: failed to showDirectory', error);
                    notification.create('error', language.get('transferActivity.unableLocalFolder'), language.get('transferActivity.unableLocalFolderHelper'));
                },
            }));
        };
        if (transfer.id) {
            this.nodeTransfer = transfer;
            if (this.nodeTransfer.status === 'running') {
                const foundSession = (this.nodeTransfer.sessions || []).filter(item => item.status === 'running')[0];
                if (foundSession && foundSession.target_rate_kbps === 0) {
                    this.nodeTransfer.status = 'paused';
                }
            }
        }
        else if (transfer.uuid) {
            this.connectTransfer = transfer;
        }
        else {
            new AsperaError('AsperaTransfer: Could not decipher if transfer was from Connect or Node', { transfer });
        }
        if (this.connectTransfer) {
            this.connectStyleTransfer = this.connectTransfer;
        }
        else {
            this.connectStyleTransfer = nodeTransferToConnectTransfer(this.nodeTransfer || {});
        }
    }
    /** The type of transfer */
    get type() {
        if (this.nodeTransfer) {
            return 'node';
        }
        else if (this.connectTransfer && this.connectTransfer.httpGatewayTransfer) {
            return 'http';
        }
        else if (this.connectTransfer && this.connectTransfer.desktopSdkTransfer) {
            return 'desktop';
        }
        else {
            return 'connect';
        }
    }
    get title() {
        var _a, _b;
        return ((_a = this.connectTransfer) === null || _a === void 0 ? void 0 : _a.title) || ((_b = this.nodeTransfer) === null || _b === void 0 ? void 0 : _b.title) || '';
    }
    /** The transferSpec of the transfer */
    get transferSpec() {
        if (this.nodeTransfer) {
            return this.nodeTransfer.start_spec;
        }
        else if (this.connectTransfer) {
            return this.connectTransfer.transfer_spec;
        }
        return undefined;
    }
    /** The status of the transfer */
    get status() {
        if (this.nodeTransfer) {
            return this.nodeTransfer.status;
        }
        else if (this.connectTransfer) {
            return this.connectTransfer.status;
        }
        return undefined;
    }
    /** The ID of the transfer */
    get id() {
        if (this.nodeTransfer) {
            return this.nodeTransfer.id;
        }
        else if (this.connectTransfer) {
            return this.connectTransfer.uuid;
        }
        return '';
    }
    /** The calculated rate in Kbps */
    get calcRateKbps() {
        var _a, _b;
        if (this.nodeTransfer) {
            let allSessionsTotal = 0;
            if (this.nodeTransfer.avg_rate_kbps) {
                return this.nodeTransfer.avg_rate_kbps;
            }
            (_a = this.nodeTransfer.sessions) === null || _a === void 0 ? void 0 : _a.forEach(session => {
                allSessionsTotal += session.calc_rate_kbps;
            });
            if (((_b = this.nodeTransfer.sessions) === null || _b === void 0 ? void 0 : _b.length) && allSessionsTotal) {
                return allSessionsTotal / this.nodeTransfer.sessions.length;
            }
        }
        else if (this.connectTransfer) {
            return this.connectTransfer.calculated_rate_kbps;
        }
        return 0;
    }
    /** The remaining time in seconds.  The app hides if 0 or not set */
    get remainingSeconds() {
        var _a;
        if (this.connectTransfer && this.connectTransfer.remaining_usec) {
            return Math.round(this.connectTransfer.remaining_usec / 1000000);
        }
        else if (this.nodeTransfer && ((_a = this.nodeTransfer.precalc) === null || _a === void 0 ? void 0 : _a.bytes_expected) && this.nodeTransfer.bytes_written && this.calcRateKbps) {
            const remainingKiloBytes = (this.nodeTransfer.precalc.bytes_expected - this.nodeTransfer.bytes_written) / 1000;
            return Math.round(remainingKiloBytes / (this.calcRateKbps * 8)) * 100;
        }
        return 0;
    }
    /** The bytes expected; for Node this may not be available */
    get bytesExpected() {
        var _a;
        if (this.nodeTransfer) {
            if (this.status === 'completed' && this.nodeTransfer.bytes_transferred) {
                return this.nodeTransfer.bytes_transferred;
            }
            return (_a = this.nodeTransfer.precalc) === null || _a === void 0 ? void 0 : _a.bytes_expected;
        }
        else if (this.connectTransfer) {
            return this.connectTransfer.bytes_expected;
        }
        return undefined;
    }
    /** The bytes written */
    get bytesWritten() {
        if (this.nodeTransfer) {
            return this.nodeTransfer.bytes_written;
        }
        else if (this.connectTransfer) {
            return this.connectTransfer.bytes_written;
        }
        return 0;
    }
    /** The number of files written to storage */
    get filesCompleted() {
        if (this.connectTransfer && this.connectTransfer.file_counts) {
            return this.connectTransfer.file_counts.completed;
        }
        else if (this.nodeTransfer) {
            return this.nodeTransfer.files_completed;
        }
        return 0;
    }
    /** The transfer system that started the transfer. Currently Connect transfers do not show anything. */
    get displayTransferSystem() {
        switch (this.type) {
            case 'http':
                return language.get('transferActivity.httpTransfer');
            case 'node':
                return language.get('transferActivity.nodeTransfer');
            default:
                return undefined;
        }
    }
    /** The name of the Aspera App that started the transfer */
    get displayAppName() {
        var _a, _b, _c;
        const appId = ((_c = (_b = (_a = this.transferSpec) === null || _a === void 0 ? void 0 : _a.tags) === null || _b === void 0 ? void 0 : _b.aspera) === null || _c === void 0 ? void 0 : _c.app) || 'aspera';
        const displayNames = {
            files: language.get('navItems.files'),
            packages: language.get('navItems.packages'),
            admin: language.get('navItems.admin'),
            automation: language.get('navItems.automation'),
            activity: language.get('navItems.activity'),
            help: language.get('navItems.help'),
            settings: language.get('navItems.settings'),
            public: language.get('navItems.public'),
            aspera: language.get('globals.aspera'),
            faspio: language.get('navItems.faspio'),
        };
        return displayNames[appId] || displayNames.aspera;
    }
    /** The display name of the transfer. For transfers started with older versions a default string will be shown */
    get displayTransferName() {
        var _a, _b, _c, _d;
        return ((_d = (_c = (_b = (_a = this.transferSpec) === null || _a === void 0 ? void 0 : _a.tags) === null || _b === void 0 ? void 0 : _b.aspera) === null || _c === void 0 ? void 0 : _c.aspera_ui) === null || _d === void 0 ? void 0 : _d.transfer_name) || language.get('transferActivity.transfer');
    }
    /** The display name of the transfer. For transfers started with older versions a default string will be shown */
    get displayTransferItem() {
        var _a;
        return ((_a = this.connectTransfer) === null || _a === void 0 ? void 0 : _a.current_file) || language.get('transferActivity.transfer');
    }
    /** The icon to show for this transfer */
    get displayIconKey() {
        var _a;
        if (this.status === 'completed') {
            return 'completed';
        }
        else if (this.status === 'failed') {
            return 'failed';
        }
        else if (((_a = this.transferSpec) === null || _a === void 0 ? void 0 : _a.direction) === 'send') {
            return 'upload';
        }
        return 'download';
    }
    /** The display text to show on the UI for summary */
    get displayDetailsStatus() {
        var _a;
        if (this.nodeTransfer) {
            switch (this.status) {
                case 'failed':
                    return language.get('transferActivity.transferFailed');
                case 'completed':
                    return language.get('transferActivity.transferCompleted');
                case 'running':
                case 'initiating':
                case 'waiting':
                    return language.get('transferActivity.transferring');
            }
        }
        else if (((_a = this.transferSpec) === null || _a === void 0 ? void 0 : _a.direction) === 'send') {
            switch (this.status) {
                case 'failed':
                    return language.get('transferActivity.uploadFailed');
                case 'completed':
                    return language.get('transferActivity.uploaded');
                case 'running':
                case 'initiating':
                case 'waiting':
                    return language.get('transferActivity.uploading');
            }
        }
        else {
            switch (this.status) {
                case 'failed':
                    return language.get('transferActivity.downloadFailed');
                case 'completed':
                    return language.get('transferActivity.downloaded');
                case 'running':
                case 'initiating':
                case 'waiting':
                    return language.get('transferActivity.downloading');
            }
        }
        switch (this.status) {
            case 'queued':
                return language.get('transferActivity.queued');
            case 'removed':
                return language.get('transferActivity.removed');
            case 'cancelled':
            case 'canceled':
                return language.get('transferActivity.stopped');
            case 'willretry':
                return language.get('transferActivity.willRetry');
            case 'partially_completed':
                return language.get('transferActivity.partiallyCompleted');
            case 'orphaned':
                return language.get('transferActivity.orphaned');
            case 'paused':
                return language.get('transferActivity.paused');
            case 'pausing':
                return language.get('transferActivity.pausing');
            case 'canceling':
                return language.get('transferActivity.canceling');
            case 'resuming':
                return language.get('transferActivity.resuming');
        }
        new AsperaError('AsperaTransfer: Encountered unknown transfer status', { status: this.status });
        return language.get('transferActivity.transferring');
    }
    /** The summary of the transfer to show */
    get displayDetailSummary() {
        var _a, _b;
        if (this.status === 'failed') {
            return language.get('transferActivity.errorItem', [((_a = this.nodeTransfer) === null || _a === void 0 ? void 0 : _a.error_desc) || ((_b = this.connectTransfer) === null || _b === void 0 ? void 0 : _b.error_desc) || language.get('transferActivity.unknownError')]);
        }
        if (this.status === 'completed') {
            const stringParts = [];
            if (this.bytesExpected) {
                stringParts.push(language.getBytes(this.bytesExpected, 1));
            }
            if (this.filesCompleted) {
                if (this.filesCompleted === 1) {
                    stringParts.push(language.get('transferActivity.oneFile'));
                }
                else {
                    stringParts.push(language.get('transferActivity.itemFiles', [this.filesCompleted]));
                }
            }
            return stringParts.join(', ');
        }
        if (this.isActive) {
            const stringParts = [];
            if (this.calcRateKbps) {
                stringParts.push(language.getBytes((this.calcRateKbps * 1000), 1, undefined, undefined, true));
            }
            if (this.bytesExpected && this.bytesWritten) {
                const displayWritten = language.getBytes(this.bytesWritten, 1);
                const displayTotal = language.getBytes(this.bytesExpected, 1);
                stringParts.push(`${displayWritten} / ${displayTotal}`);
            }
            return stringParts.join(' | ');
        }
        return '';
    }
    /** Indicate if a progress bar can be rendered safely */
    get supportProgressBar() {
        return !!(this.isActive && this.bytesExpected && this.bytesWritten);
    }
    /** Indicate if transfer is considered active */
    get isActive() {
        return (this.status === 'running' || this.status === 'initiating' || this.status === 'waiting' || this.status === 'queued');
    }
    /** Transfer title */
    get transferTitle() {
        return this.title || this.displayTransferName;
    }
    /** App key that started the transfer */
    get appType() {
        var _a, _b, _c, _d, _e, _f;
        if ((_c = (_b = (_a = this.transferSpec) === null || _a === void 0 ? void 0 : _a.tags) === null || _b === void 0 ? void 0 : _b.aspera) === null || _c === void 0 ? void 0 : _c.files) {
            return 'AOC';
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        else if ((_f = (_e = (_d = this.transferSpec) === null || _d === void 0 ? void 0 : _d.tags) === null || _e === void 0 ? void 0 : _e.aspera) === null || _f === void 0 ? void 0 : _f.faspex) {
            return 'ENTERPRISE';
        }
        return 'OTHER_WEB';
    }
    /** User friendly name of the app that started the transfer */
    get appName() {
        var _a, _b, _c, _d;
        switch (this.appType) {
            case 'AOC':
                if ((_d = (_c = (_b = (_a = this.transferSpec) === null || _a === void 0 ? void 0 : _a.tags) === null || _b === void 0 ? void 0 : _b.aspera) === null || _c === void 0 ? void 0 : _c.files) === null || _d === void 0 ? void 0 : _d.on_prem) {
                    return language.get('transferInfo.asperaOnCloudOnPrem', [this.displayAppName]);
                }
                else {
                    return language.get('transferInfo.asperaOnCloud', [this.displayAppName]);
                }
            case 'ENTERPRISE':
                return language.get('transferInfo.asperaEnterprise', [this.displayAppName]);
            case 'OTHER_WEB':
            default:
                return language.get('transferInfo.otherWebApp');
        }
    }
    /** Indicate if transfer can be removed from monitor */
    get canRemove() {
        return !this.isActive;
    }
    /** The percent of the transfer (0-100) */
    get safePercent() {
        let testPercent = 0;
        if (this.connectTransfer && typeof this.connectTransfer.percentage === 'number') {
            testPercent = this.connectTransfer.percentage * 100;
        }
        else if (typeof this.bytesWritten === 'number' && typeof this.bytesExpected === 'number') {
            testPercent = (this.bytesWritten / this.bytesExpected) * 100;
        }
        if (testPercent > 100) {
            testPercent = 100;
        }
        else if (testPercent < 0) {
            testPercent = 0;
        }
        return testPercent;
    }
    /**
     * Same as safePercent but is rounded to a whole number.
     */
    get safeRoundedPercent() {
        if (this.safePercent) {
            return Math.floor(this.safePercent);
        }
        return this.safePercent;
    }
    /** The Estimated time of completion */
    get displayEta() {
        return getTimeString(this.remainingSeconds);
    }
    get runningInDesktop() {
        return !!getEnvironmentVar('desktop_mode');
    }
    /**
     * Indicate if a transfer can be canceled
     *  */
    get canCancel() {
        const transferFunction = !!getTransferSystemData().cancelRemoteTransfer;
        return !!((((this.type === 'connect' || this.type === 'desktop') || this.runningInDesktop) || this.type === 'http' || (this.type === 'node' && transferFunction)) && (this.status === 'running' || this.status === 'initiating' || this.status === 'queued' || this.status === 'willretry' || this.status === 'waiting'));
    }
    /**
     * Indicate if a transfer can be paused
     *  */
    get canPause() {
        var _a, _b;
        const transferFunction = !!getTransferSystemData().pauseRemoteTransfer;
        if (runningWithAsperaMobile() && !((_b = (_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.transferSystemData) === null || _b === void 0 ? void 0 : _b.disableConnect)) {
            return false;
        }
        if ((this.type === 'connect' || this.type === 'desktop') || this.runningInDesktop) {
            return this.status === 'running' || this.status === 'initiating' || this.status === 'queued' || this.status === 'willretry' || this.status === 'waiting';
        }
        else if (this.type === 'node' && transferFunction) {
            return this.status === 'running';
        }
        return false;
    }
    /**
     * Indicate if a transfer can be resumed
     *  */
    get canResume() {
        var _a, _b;
        const transferFunction = !!getTransferSystemData().resumeRemoteTransfer;
        if (runningWithAsperaMobile() && !((_b = (_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.transferSystemData) === null || _b === void 0 ? void 0 : _b.disableConnect)) {
            return false;
        }
        if ((this.type === 'connect' || this.type === 'desktop') || this.runningInDesktop) {
            return this.status === 'cancelled' || this.status === 'canceled' || this.status === 'paused';
        }
        else if (this.type === 'node' && transferFunction) {
            return this.status === 'paused';
        }
        return false;
    }
    /** Indicate if a transfer can show local folder */
    get canOpenLocalFolder() {
        var _a;
        return !!(((this.type === 'connect' || this.type === 'desktop') || this.runningInDesktop) && this.status === 'completed' && ((_a = this.transferSpec) === null || _a === void 0 ? void 0 : _a.direction) === 'receive');
    }
    /** Indicate if a transfer can have rate changes updated */
    get canUpdateTransfer() {
        return !!(this.connectTransfer && !this.connectTransfer.httpGatewayTransfer && this.isActive && !runningWithAsperaMobile());
    }
}
